import { SnackbarProvider } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import backend from './api';
import { setUserData } from './redux-store/auth/actions';
import { ScrollToTop } from './Hooks/usePreventScrolling';
import { HelmetProvider } from 'react-helmet-async';

import { setCompanyInfo } from './redux-store/auth/actions';
import AuthInvitee from './MainWebsite/Pages/Signup/AuthInvitee';
import InvitationExpired from './Panels/GlobalComponents/InvitationExpired/InvitationExpired';
import DisabledUser from './Panels/GlobalComponents/DisabledUser/DisabledUser';
import SuccessPopup from './Panels/GlobalComponents/SuccessPopup/SuccessPopup';

import './App.css'

// Logout out Routes
import Home from './MainWebsite/Pages/Home/Home';
import Signup from './MainWebsite/Pages/Signup/Signup';
import Login from './MainWebsite/Pages/Login/Login';
import CompanySignup from './MainWebsite/Pages/Company/Signup/Signup';
import CompanyLogin from './MainWebsite/Pages/Company/Login/Login';
import CreateProfile from './MainWebsite/Pages/Onboard/Customer/CreateProfile';
import OnboardMembership from './MainWebsite/Pages/Onboard/Membership/Membership';
import ContactUs from './MainWebsite/Pages/ContactUs/ContactUs';
import PrivacyPolicy from './MainWebsite/Pages/PrivacyPolicy/PrivacyPolicy';
import TermsOfService from './MainWebsite/Pages/TermsOfService/TermsOfService';
import DataSecurity from './MainWebsite/Pages/DataSecurity/DataSecurity';

// Admin and Employee pages
import AdminMainLayout from './Panels/Admin/MainLayout/MainLayout';
import AdminDashboard from './Panels/Admin/Pages/Activity/Dashboard/Dashboard';
import AdminMessages from './Panels/Admin/Pages/Activity/Messages/Messages';
import AdminAppointments from './Panels/Admin/Pages/Activity/Appointments/Appointments';
import AdminAddAppointment from './Panels/Admin/Pages/Activity/Appointments/AddAppointment/AddAppointment';
import AdminAppointmentDetails from './Panels/Admin/Pages/Activity/Appointments/AppointmentDetails/AppointmentDetails';
import AdminClients from './Panels/Admin/Pages/Activity/Clients/Clients';
import AdminClientDetail from './Panels/Admin/Pages/Activity/Clients/ClientDetail/ClientDetail';
import AdminAddClient from './Panels/Admin/Pages/Activity/Clients/AddClient/AddClient';
import AdminInvoices from './Panels/Admin/Pages/Activity/Invoices/Invoices';
import AdminAddInvoice from './Panels/Admin/Pages/Activity/Invoices/AddInvoicePage/AddInvoice';
import AdminViewInvoice from './Panels/Admin/Pages/Activity/Invoices/ViewInvoicePage/ViewInvoicePage';
import AdminRoutes from './Panels/Admin/Pages/Activity/Routes/Routes';
import AdminTeam from './Panels/Admin/Pages/Company/Team/Team';
import Information from './Panels/Admin/Pages/Company/Information/Information';
import AdminSettings from './Panels/Admin/Pages/Account/Settings/Settings';
import AdminAvailability from './Panels/Admin/Pages/Company/Availability/Availibility';

// Customer pages
import CustomerMainLayout from './Panels/Customer/MainLayout/MainLayout';
import CustomerDashboard from './Panels/Customer/Pages/Activity/Dashboard/Dashboard';
import CustomerMessages from './Panels/Customer/Pages/Activity/Messages/Messages';
import CustomerAppointments from './Panels/Customer/Pages/Activity/Appointments/Appointments';
import CustomerAddAppointment from './Panels/Customer/Pages/Activity/Services/AddAppointment/AddAppointment';
import CustomerInvoices from './Panels/Customer/Pages/Activity/Invoices/Invoices';
import CustomerViewInvoice from './Panels/Customer/Pages/Activity/Invoices/ViewInvoicePage/ViewInvoicePage';
import CustomerSettings from './Panels/Customer/Pages/Account/Settings/Settings';
import CustomerServices from './Panels/Customer/Pages/Activity/Services/Services';
import MobileOpenGate from './utils/MobileOpenGate';

import withSEO from '../src/seo/withSEO';
import { ADMIN_PAGE_META, CUSTOMER_PAGE_META } from '../src/seo/SEO';

import TipPopup from './Panels/Customer/Pages/Activity/Dashboard/Comp/RecordTable/TipPopup/TipPopup';
import HowItWorks from './MainWebsite/Pages/HowItWorks/HowItWorks';
import Testimonials from './MainWebsite/Pages/Testimonials/Testimonials';
import Footer from './MainWebsite/GlobalComp/Footer/Footer';

const RenderRouterWithMenuFooter = (Component) => (
  <>
    <Component />
    <Footer />
  </>
);

const MainWebsiteLoggedOutRoutes = () => (
  <>
    <Routes>
      <Route path='/' element={RenderRouterWithMenuFooter(Home)} ></Route>
      <Route path='/how-it-works' element={RenderRouterWithMenuFooter(HowItWorks)} ></Route>
      <Route path='/testimonials' element={RenderRouterWithMenuFooter(Testimonials)} ></Route>
      <Route path='/signup' element={<Signup />} />
      <Route path='/payment/success' element={<SuccessPopup />} />
      <Route path='/subscription/success' element={<SuccessPopup />} />

      <Route path='/login' element={<Login />} />
      <Route path="/:hashcode/:senderId/:inviteeId/:parentcompanyId" element={<CompanySignup />} />
      <Route path="/invitationexpired" element={<InvitationExpired />} />
      <Route path="/accessdenied" element={<DisabledUser />} />


      <Route path='/onboard/customer' element={<CreateProfile />} />
      <Route path='/onboard/membership' element={<OnboardMembership />} />
      <Route path='/company/signup' element={<CompanySignup />} />
      <Route path='/company/login' element={<CompanyLogin />} />
      <Route path='/contact-us' element={<ContactUs />} />
      <Route path='/privacy-policy' element={<PrivacyPolicy />} />
      <Route path='/terms-of-service' element={<TermsOfService />} />
      <Route path='/data-security' element={<DataSecurity />} />
      <Route path="/view-invoice/:id" element={<CustomerViewInvoice />} />
      <Route path="/invoices/:id" element={<CustomerViewInvoice />} />
    <Route path="/tip/:bookingId" element={CustomerWithSideBar(withSEO(TipPopup, CUSTOMER_PAGE_META, '/tip/:bookingId'))} />


      <Route path='*' element={RenderRouterWithMenuFooter(Home)} ></Route>
    </Routes>
  </>
)

const AdminWithSideBar = (Component) => (
  <AdminMainLayout component={<Component />} />
);

const AdminLoggedInRoutes = () => (
  <Routes>
    <Route path="/" element={AdminWithSideBar(withSEO(AdminDashboard, ADMIN_PAGE_META, '/'))} />

    <Route path="/appointments" element={AdminWithSideBar(withSEO(AdminAppointments, ADMIN_PAGE_META, '/appointments'))} />
    <Route path="/add-appointment" element={AdminWithSideBar(withSEO(AdminAddAppointment, ADMIN_PAGE_META, '/add-appointment'))} />
    <Route path="/add-appointment/:clientID" element={AdminWithSideBar(withSEO(AdminAddAppointment, ADMIN_PAGE_META, '/add-appointment/:clientID'))} />
    <Route path="/edit-appointment/:id" element={AdminWithSideBar(withSEO(AdminAddAppointment, ADMIN_PAGE_META, '/edit-appointment/:id'))} />
    <Route path="/view-appointment/:tab/:id" element={AdminWithSideBar(withSEO(AdminAppointmentDetails, ADMIN_PAGE_META, '/view-appointment/:tab/:id'))} />

    <Route path="/clients" element={AdminWithSideBar(withSEO(AdminClients, ADMIN_PAGE_META, '/clients'))} />
    <Route path="/client-detail/:tab/:id" element={AdminWithSideBar(withSEO(AdminClientDetail, ADMIN_PAGE_META, '/client-detail/:tab/:id'))} />
    <Route path="/add-client" element={AdminWithSideBar(withSEO(AdminAddClient, ADMIN_PAGE_META, '/add-client'))} />
    <Route path="/edit-client/:id" element={AdminWithSideBar(withSEO(AdminAddClient, ADMIN_PAGE_META, '/edit-client/:id'))} />

    <Route path="/invoices" element={AdminWithSideBar(withSEO(AdminInvoices, ADMIN_PAGE_META, '/invoices'))} />
    <Route path="/add-invoice" element={AdminWithSideBar(withSEO(AdminAddInvoice, ADMIN_PAGE_META, '/add-invoice'))} />
    <Route path="/add-invoice/:clientID" element={AdminWithSideBar(withSEO(AdminAddInvoice, ADMIN_PAGE_META, '/add-invoice/:clientID'))} />
    <Route path="/edit-invoice/:id" element={AdminWithSideBar(withSEO(AdminAddInvoice, ADMIN_PAGE_META, '/edit-invoice/:id'))} />
    <Route path="/view-invoice/:id" element={AdminWithSideBar(withSEO(AdminViewInvoice, ADMIN_PAGE_META, '/view-invoice/:id'))} />
    <Route path="/invoices/:id" element={AdminWithSideBar(withSEO(AdminViewInvoice, ADMIN_PAGE_META, '/view-invoice/:id'))} />

    <Route path="/availability" element={AdminWithSideBar(withSEO(AdminAvailability, ADMIN_PAGE_META, '/availability'))} />
    <Route path="/messages" element={AdminWithSideBar(withSEO(AdminMessages, ADMIN_PAGE_META, '/messages'))} />
    <Route path="/messages/:userId" element={AdminWithSideBar(withSEO(AdminMessages, ADMIN_PAGE_META, '/messages/:userId'))} />
    <Route path="/routes" element={AdminWithSideBar(withSEO(AdminRoutes, ADMIN_PAGE_META, '/routes'))} />

    <Route path="/team" element={AdminWithSideBar(withSEO(AdminTeam, ADMIN_PAGE_META, '/team'))} />
    <Route path="/information" element={AdminWithSideBar(withSEO(Information, ADMIN_PAGE_META, '/information'))} />
    <Route path="/settings/:tab" element={AdminWithSideBar(withSEO(AdminSettings, ADMIN_PAGE_META, '/settings/:tab'))} />

    <Route path="*" element={AdminWithSideBar(withSEO(AdminDashboard, ADMIN_PAGE_META, '*'))} />
    <Route path='/home' element={RenderRouterWithMenuFooter(Home)} ></Route>


    <Route path="/contact-us" element={<ContactUs />} />
  </Routes>
);

const CustomerWithSideBar = (Component) => (
  <CustomerMainLayout component={<Component />} />
);

const CustomerLoggedInRoutes = () => (
  <Routes>
    <Route path="/" element={CustomerWithSideBar(withSEO(CustomerDashboard, CUSTOMER_PAGE_META, '/'))} />
    <Route path="/dashboard" element={CustomerWithSideBar(withSEO(CustomerDashboard, CUSTOMER_PAGE_META, '/dashboard'))} />
    <Route path="/tip/:bookingId" element={CustomerWithSideBar(withSEO(TipPopup, CUSTOMER_PAGE_META, '/tip/:bookingId'))} />

    <Route path="/messages" element={CustomerWithSideBar(withSEO(CustomerMessages, CUSTOMER_PAGE_META, '/messages'))} />
    <Route path="/messages/:userId" element={CustomerWithSideBar(withSEO(CustomerMessages, CUSTOMER_PAGE_META, '/messages/:userId'))} />

    <Route path="/appointments" element={CustomerWithSideBar(withSEO(CustomerAppointments, CUSTOMER_PAGE_META, '/appointments'))} />
    <Route path="/invoices" element={CustomerWithSideBar(withSEO(CustomerInvoices, CUSTOMER_PAGE_META, '/invoices'))} />
    <Route path="/view-invoice/:id" element={CustomerWithSideBar(withSEO(CustomerViewInvoice, CUSTOMER_PAGE_META, '/view-invoice/:id'))} />
    <Route path="/services" element={CustomerWithSideBar(withSEO(CustomerServices, CUSTOMER_PAGE_META, '/services'))} />
    <Route path="/add-appointment" element={CustomerWithSideBar(withSEO(CustomerAddAppointment, CUSTOMER_PAGE_META, '/add-appointment'))} />

    <Route path="/settings/:tab" element={CustomerWithSideBar(withSEO(CustomerSettings, CUSTOMER_PAGE_META, '/settings/:tab'))} />

    <Route path="*" element={CustomerWithSideBar(withSEO(CustomerDashboard, CUSTOMER_PAGE_META, '*'))} />
    <Route path='/home' element={RenderRouterWithMenuFooter(Home)} ></Route>

    {/* Demo-only public pages */}
    <Route path="/contact-us" element={<ContactUs />} />
    <Route path="/onboard/membership" element={withSEO(OnboardMembership, CUSTOMER_PAGE_META, '/onboard/membership')()} />
    <Route path="/onboard/customer" element={withSEO(CreateProfile, CUSTOMER_PAGE_META, '/onboard/customer')()} />
  </Routes>
);

const CustomerNotOnboardedRoutes = () => (
  <Routes>
    <Route path="/onboard/customer" element={withSEO(CreateProfile, CUSTOMER_PAGE_META, '/onboard/customer')()} />
    <Route path="/onboard/membership" element={withSEO(OnboardMembership, CUSTOMER_PAGE_META, '/onboard/membership')()} />
    <Route path="/" element={<Navigate to="/onboard/customer" replace />} />
  </Routes>
);


function AppRoutes() {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth);
  const onboarding = useSelector((state) => state.companyInfo.info);
  const companyId = onboarding?._id;

  useEffect(() => {
    if (user) {
      const userId = user?._id;
      const fetchUser = async () => {
        const res = await backend.get(`/api/user/${userId}`);
        const updated = { ...user, ...res.data.data };
        await dispatch(setUserData(updated));
      }
      fetchUser();
    }
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {

        if (companyId) {
          // Check if companyName exists in database
          const response = await backend.get(`/api/onboarding/${companyId}`);
          // If response.data is not empty, companyName exists in database
          if (response?.data) {
            await dispatch(setCompanyInfo(response.data.data))
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []); // Empty dependency array ensures useEffect runs once on component mount

  return (
    <>
      <BrowserRouter>
        <HelmetProvider>
          {/* <MobileOpenGate /> */}
          <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
            <ScrollToTop />
            {user && user.role == 'company' ?
              <AdminLoggedInRoutes /> : user && user.role == 'employee' ? <AdminLoggedInRoutes /> :
                user && user.role == 'customer' && user.onboarded === true ?
                  <CustomerLoggedInRoutes /> : user && user.role == 'customer' && user.onboarded === false ? <CustomerNotOnboardedRoutes /> : <MainWebsiteLoggedOutRoutes />
            }
          </SnackbarProvider>
        </HelmetProvider>
      </BrowserRouter>
    </>
  );
}

export default AppRoutes;
